import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosResponse } from 'axios'
import { toast } from 'react-toastify'
import {
  ISiteNetworkAcceptProposalRequest,
  ISiteNetworkActivateDraftResponse,
  ISiteNetworkCreateRoadEventRequest,
  ISiteNetworkGraph,
  ISiteNetworkImportSegmentRequest,
  ISiteNetworkLocation,
  ISiteNetworkProposal,
  ISiteNetworkRoadEvent,
  ISiteNetworkRouteRequest,
  ISiteNetworkRouteResponse,
} from 'types/api/siteNetworkApi.interface'
import { ISiteNetworkState } from 'types/siteNetwork.interface'
import { baseUrl } from 'utils/axios'
import { handleErrors } from 'utils/helpers'

const PREFIX = 'site-network/network'

const initialState: ISiteNetworkState = {
  isLoading: false,
  graph: null,
  draftGraph: null,
  locations: [],
  roadEvents: [],
  proposals: [],
  route: null,
  isRouting: false,
}

export const getNetworkGraph = createAsyncThunk(
  'siteNetwork/getNetworkGraph',
  async (_, thunkAPI) => {
    try {
      const resp: AxiosResponse<ISiteNetworkGraph> = await baseUrl.get(`${PREFIX}/graph`, {
        params: { status: 'ACTIVE' },
      })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getNetworkDraftGraph = createAsyncThunk(
  'siteNetwork/getNetworkDraftGraph',
  async (_, thunkAPI) => {
    try {
      const resp: AxiosResponse<ISiteNetworkGraph> = await baseUrl.get(`${PREFIX}/graph`, {
        params: { status: 'DRAFT' },
      })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getSiteNetworkLocations = createAsyncThunk(
  'siteNetwork/getSiteNetworkLocations',
  async (_, thunkAPI) => {
    try {
      const resp: AxiosResponse<ISiteNetworkLocation[]> = await baseUrl.get(`${PREFIX}/locations`)
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getRoadEvents = createAsyncThunk('siteNetwork/getRoadEvents', async (_, thunkAPI) => {
  try {
    const resp: AxiosResponse<ISiteNetworkRoadEvent[]> = await baseUrl.get(
      `${PREFIX}/road-events`,
      {
        params: { active: true },
      },
    )
    return resp.data
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const getProposals = createAsyncThunk('siteNetwork/getProposals', async (_, thunkAPI) => {
  try {
    const resp: AxiosResponse<ISiteNetworkProposal[]> = await baseUrl.get(`${PREFIX}/proposals`)
    return resp.data
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const calculateRoute = createAsyncThunk(
  'siteNetwork/calculateRoute',
  async (req: ISiteNetworkRouteRequest, thunkAPI) => {
    try {
      const resp: AxiosResponse<ISiteNetworkRouteResponse> = await baseUrl.post(
        `${PREFIX}/route`,
        req,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createRoadEvent = createAsyncThunk(
  'siteNetwork/createRoadEvent',
  async (body: ISiteNetworkCreateRoadEventRequest, thunkAPI) => {
    try {
      const resp: AxiosResponse = await baseUrl.post(`${PREFIX}/road-events`, body)
      await thunkAPI.dispatch(getRoadEvents())
      await thunkAPI.dispatch(getNetworkGraph())
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const cancelRoadEvent = createAsyncThunk(
  'siteNetwork/cancelRoadEvent',
  async (id: string, thunkAPI) => {
    try {
      const resp: AxiosResponse = await baseUrl.delete(`${PREFIX}/road-events/${id}`, {
        data: { reason: 'canceled from UI' },
      })
      await thunkAPI.dispatch(getRoadEvents())
      await thunkAPI.dispatch(getNetworkGraph())
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const importDrawnSegment = createAsyncThunk(
  'siteNetwork/importDrawnSegment',
  async (p: ISiteNetworkImportSegmentRequest, thunkAPI) => {
    try {
      const properties: any = { class: p.cls, name: p.name ?? null }
      properties['one_way'] = p.oneWay ?? false
      const resp: AxiosResponse = await baseUrl.post(`${PREFIX}/import`, {
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: { type: 'LineString', coordinates: p.coordinates },
            properties,
          },
        ],
      })
      await thunkAPI.dispatch(getNetworkDraftGraph())
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const activateDrafts = createAsyncThunk(
  'siteNetwork/activateDrafts',
  async (_, thunkAPI) => {
    try {
      const resp: AxiosResponse<ISiteNetworkActivateDraftResponse> = await baseUrl.post(
        `${PREFIX}/activate-draft`,
      )
      await thunkAPI.dispatch(getNetworkGraph())
      await thunkAPI.dispatch(getNetworkDraftGraph())
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const acceptProposal = createAsyncThunk(
  'siteNetwork/acceptProposal',
  async (p: ISiteNetworkAcceptProposalRequest, thunkAPI) => {
    try {
      const resp: AxiosResponse = await baseUrl.post(`${PREFIX}/proposals/${p.id}/accept`, {
        class: p.cls,
        name: p.name,
      })
      await thunkAPI.dispatch(getProposals())
      await thunkAPI.dispatch(getNetworkDraftGraph())
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const dismissProposal = createAsyncThunk(
  'siteNetwork/dismissProposal',
  async (id: string, thunkAPI) => {
    try {
      const resp: AxiosResponse = await baseUrl.post(`${PREFIX}/proposals/${id}/dismiss`)
      await thunkAPI.dispatch(getProposals())
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const siteNetworkSlice = createSlice({
  name: 'siteNetwork',
  initialState,
  reducers: {
    clearSiteNetwork: (state) => {
      state.graph = null
      state.draftGraph = null
      state.locations = []
      state.roadEvents = []
      state.proposals = []
      state.route = null
    },
    clearSiteNetworkRoute: (state) => {
      state.route = null
    },
  },
  extraReducers: {
    [getNetworkGraph.pending.type]: (state) => {
      state.isLoading = true
    },
    [getNetworkGraph.fulfilled.type]: (state, { payload }: { payload: ISiteNetworkGraph }) => {
      state.isLoading = false
      state.graph = payload
    },
    [getNetworkGraph.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getNetworkDraftGraph.fulfilled.type]: (state, { payload }: { payload: ISiteNetworkGraph }) => {
      state.draftGraph = payload
    },
    [getNetworkDraftGraph.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [getSiteNetworkLocations.fulfilled.type]: (
      state,
      { payload }: { payload: ISiteNetworkLocation[] },
    ) => {
      state.locations = payload
    },
    [getSiteNetworkLocations.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [getRoadEvents.fulfilled.type]: (state, { payload }: { payload: ISiteNetworkRoadEvent[] }) => {
      state.roadEvents = payload
    },
    [getRoadEvents.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [getProposals.fulfilled.type]: (state, { payload }: { payload: ISiteNetworkProposal[] }) => {
      state.proposals = payload
    },
    [getProposals.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [calculateRoute.pending.type]: (state) => {
      state.isRouting = true
    },
    [calculateRoute.fulfilled.type]: (
      state,
      { payload }: { payload: ISiteNetworkRouteResponse },
    ) => {
      state.isRouting = false
      state.route = payload
    },
    [calculateRoute.rejected.type]: (state, { payload }) => {
      state.isRouting = false
      handleErrors(payload)
    },
    [createRoadEvent.fulfilled.type]: () => {
      toast.success('Road event created — routing updated')
    },
    [createRoadEvent.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [cancelRoadEvent.fulfilled.type]: () => {
      toast.success('Road event canceled')
    },
    [cancelRoadEvent.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [importDrawnSegment.fulfilled.type]: () => {
      toast.success('Road saved as DRAFT')
    },
    [importDrawnSegment.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [activateDrafts.fulfilled.type]: (
      _state,
      { payload }: { payload: ISiteNetworkActivateDraftResponse },
    ) => {
      toast.success(
        `Activated ${payload.activated.segments} segments / ${payload.activated.nodes} nodes`,
      )
    },
    [activateDrafts.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [acceptProposal.fulfilled.type]: () => {
      toast.success('Proposal accepted — segment created as DRAFT')
    },
    [acceptProposal.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [dismissProposal.fulfilled.type]: () => {
      toast.success('Proposal dismissed')
    },
    [dismissProposal.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
  },
})

export const { clearSiteNetwork, clearSiteNetworkRoute } = siteNetworkSlice.actions

export default siteNetworkSlice.reducer
